import React from "react";
import { useParams } from "react-router";

import { AutoFormFieldProps } from "@autoform/react";

import { EnrichmentSummary } from "@/components/enrichment/enrichment-summary";

export type EnrichmentSummaryFieldCustomData = {
  /** Refine resource name, e.g. "artists". Defaults to the route's first segment. */
  resource?: string;
  /** Entity id. Defaults to the route's `:id` param, then to its last segment. */
  id?: string;
};

/** `"/artists/enrichment/abc"` -> `["artists", "enrichment", "abc"]`. */
function routeSegments(): string[] {
  if (typeof window === "undefined") return [];
  return window.location.pathname.split("/").filter(Boolean);
}

/**
 * Read-only `EnrichmentSummary` card inside an AutoForm.
 * Register globally as the `enrichmentSummary` fieldType.
 *
 * The field's own value is the enrichment object, so the card always shows the
 * values the form is about to submit — not the ones it was loaded with. The
 * card reads and never writes: no `setValue`, no `inputProps.onChange`, so the
 * submitted value is exactly the one the schema resolved.
 */
export const EnrichmentSummaryField: React.FC<AutoFormFieldProps> = ({
  value,
  field,
}) => {
  const params = useParams<{ id?: string }>();
  const customData = field.fieldConfig?.customData as
    | EnrichmentSummaryFieldCustomData
    | undefined;

  const segments = routeSegments();
  const resource = customData?.resource ?? segments[0];
  const id = customData?.id ?? params?.id ?? segments[segments.length - 1];

  if (!resource || !id) return null;

  return (
    <EnrichmentSummary
      resource={resource}
      id={id}
      enrichment={
        value && typeof value === "object" && !Array.isArray(value)
          ? (value as Record<string, unknown>)
          : null
      }
    />
  );
};

export default EnrichmentSummaryField;
